import { createContext, useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { empresasService } from "app/services";
import { ErrorMessage, Spinner } from "app/components";

const ActividadesEconomicasContext = createContext();

export function ActividadesEconomicasProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const actividadesEconomicasQuery = useQuery({
    queryKey: ["empresa:get:actividades-economicas", params.empresa_id],
    queryFn: () =>
      empresasService.getActividadesEconomicas({
        empresaId: params.empresa_id,
      }),
  });

  const actividadEconomicaAddMutation = useMutation({
    queryKey: ["empresa:add:actividad-economica", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addActividadEconomica(data), {
        loading: "Agregando la actividad económica...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await actividadesEconomicasQuery.refetch();
    },
  });

  const actividadEconomicaUpdateMutation = useMutation({
    queryKey: ["empresa:update:actividad-economica"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateActividadEconomica(data), {
        loading: "Actualizando la actividad económica...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await actividadesEconomicasQuery.refetch();
    },
  });

  const actividadEconomicaDeleteMutation = useMutation({
    queryKey: ["empresa:delete:actividad-economica"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteActividadEconomica(data), {
        loading: "Eliminando la actividad económica...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await actividadesEconomicasQuery.refetch();
    },
  });

  const actividadEconomicaVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:actividad-economica"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyActividadEconomica(data), {
        loading: "Verificando la actividad económica...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await actividadesEconomicasQuery.refetch();
    },
  });

  async function addActividadEconomica({ actividadEconomica }) {
    return await actividadEconomicaAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      actividadEconomica,
    });
  }

  async function updateActividadEconomica({ actividadId, actividadEconomica }) {
    const { actividad_economica_verificada, ...rest } = actividadEconomica;

    return await actividadEconomicaUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      actividadId,
      actividadEconomica: rest,
    });
  }

  async function deleteActividadEconomica({ actividadId }) {
    const deleted = window.confirm("¿Desea eliminar esta actividad económica?");

    if (!!deleted) {
      await actividadEconomicaDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        actividadId,
      });

      return true;
    }

    return false;
  }

  async function verifyActividadEconomica({ actividadId }) {
    const confirmed = window.confirm(
      "¿Desea verificar esta actividad económica?"
    );

    if (confirmed) {
      return await actividadEconomicaVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        actividadId,
      });
    }

    return false;
  }

  function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!actividadesEconomicasQuery.isLoading) {
    return <Spinner label="Cargando las actividades económicas..." />;
  } else if (!!actividadesEconomicasQuery.isError) {
    return (
      <ErrorMessage
        message={actividadesEconomicasQuery.error.response.statusText}
      />
    );
  }

  return (
    <ActividadesEconomicasContext.Provider
      value={{
        actividadesEconomicas: actividadesEconomicasQuery.data,
        isLoading: actividadesEconomicasQuery.isLoading,
        isFetching: actividadesEconomicasQuery.isFetching,
        isRefetching: actividadesEconomicasQuery.isRefetching,
        addActividadEconomica,
        isAdding: actividadEconomicaAddMutation.isLoading,
        updateActividadEconomica,
        isUpdating: actividadEconomicaUpdateMutation.isLoading,
        deleteActividadEconomica,
        isDeleting: actividadEconomicaDeleteMutation.isLoading,
        verifyActividadEconomica,
        isVerifing: actividadEconomicaVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </ActividadesEconomicasContext.Provider>
  );
}

export function useActividadesEconomicasContext() {
  const actividadesEconomicasContext = useContext(ActividadesEconomicasContext);

  if (!actividadesEconomicasContext) {
    throw new Error(
      "useActividadesEconomicasContext should be used inside the ActividadesEconomicasProvider."
    );
  }

  return actividadesEconomicasContext;
}
